import { useToolListingStatus } from '@/hooks/useToolListingStatus';
import { useToolCommunityMetadata } from './Share/useWorkflowCommunityMetadata';
import {
  CommunityPublishStatusBanner,
  deriveCommunityPublishBannerType,
  OrgPublishedToolBanner,
} from './Share/CommunityPublishStatusBanner';

interface PublishedToolCommunityBannerProps {
  /** The tool's `weave_app_id` (= its `WorkflowTool.id`). */
  toolId: string;
  /**
   * Off the canvas (`/tool`) the listing read can fail for an owner without workflow edit access, so
   * Community metadata is still read there either way.
   */
  inWorkflowEditor?: boolean;
  toolName: string;
  /** The parent workflow id, for analytics. The /tool page never populates the workflow store. */
  workflowId: string;
}

/** Owner-facing banner when the tool is published to (or pending on) Community, or published to a Figma
 *  org. Self-gates on publish state; the caller gates placement (owner only). */
export const PublishedToolCommunityBanner = ({
  toolId,
  toolName,
  workflowId,
  inWorkflowEditor = false,
}: PublishedToolCommunityBannerProps) => {
  const { data: listing, isOrgScopedPublishingEnabled } = useToolListingStatus(toolId);
  const orgDestination = listing?.destination?.type === 'org' ? listing.destination : null;
  const isOrgListing = orgDestination != null;
  // An org publish is listed as `org_private`; anything else is not live in the org.
  const liveOrgDestination = listing?.publishing_status === 'org_private' ? orgDestination : null;
  const shouldFetchCommunityMetadata =
    (!inWorkflowEditor && !isOrgListing) || !isOrgScopedPublishingEnabled || listing?.destination?.type === 'community';
  const { data: metadata } = useToolCommunityMetadata(shouldFetchCommunityMetadata ? toolId : undefined);

  if (liveOrgDestination) {
    return (
      <OrgPublishedToolBanner
        toolId={toolId}
        workflowId={workflowId}
        toolName={toolName}
        orgName={liveOrgDestination.name}
        orgPlanId={liveOrgDestination.plan_id}
      />
    );
  }

  const bannerType = deriveCommunityPublishBannerType(metadata);
  if (!metadata || bannerType === null) return null;

  return (
    <CommunityPublishStatusBanner
      bannerType={bannerType}
      metadata={metadata}
      resource={{ kind: 'tool', toolId, workflowId, name: toolName }}
    />
  );
};
